import * as THREE from "three";
import type {
  CollectionConfig,
  CowryTraits,
  FinishDefinition,
  PatternAlgorithm,
} from "../core/types";
import type { PbrTextureSet, TextureArtifact, TextureKind } from "./textureTypes";

export interface TextureGenerationOptions {
  width: number;
  height: number;
  ventralMaterialEmphasis?: boolean;
  ventralIvory?: boolean;
}

export function generateTextureSet(
  collection: CollectionConfig,
  traits: CowryTraits,
  itemSeed: string,
  options: TextureGenerationOptions,
): PbrTextureSet {
  const {
    width,
    height,
    ventralMaterialEmphasis = false,
    ventralIvory = false,
  } = options;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 2 || height < 2) {
    throw new RangeError("Texture dimensions must be integers of at least 2 pixels.");
  }

  const palette = collection.paletteDefinitions[traits.paletteFamily];
  const finish = collection.finishDefinitions[traits.finishFamily];
  const pattern = collection.patternDefinitions[traits.patternFamily];
  if (!palette) throw new Error(`Collection palette definition is missing: ${traits.paletteFamily}`);
  if (!finish) throw new Error(`Collection finish definition is missing: ${traits.finishFamily}`);
  if (!pattern) throw new Error(`Collection pattern definition is missing: ${traits.patternFamily}`);
  const pixelCount = width * height;
  const baseColor = new Uint8Array(pixelCount * 4);
  const metallicRoughness = new Uint8Array(pixelCount * 4);
  const heightField = new Float32Array(pixelCount);
  const seedPhase = hashUnit(itemSeed) * Math.PI * 2;

  for (let y = 0; y < height; y += 1) {
    const v = y / (height - 1);
    for (let x = 0; x < width; x += 1) {
      const u = x / (width - 1);
      const pixel = y * width + x;
      const patternValue = samplePattern(pattern.algorithm, u, v, traits, seedPhase);
      const detail = valueNoise(u, v, itemSeed);
      let color = applyFinish(
        finish,
        mixPalette(palette.primary, palette.secondary, palette.accent, patternValue),
        detail,
      );
      const ventralMask = ventralMaterialEmphasis ? sampleVentralMask(v) : 0;
      color = applyVentralColorShift(color, ventralMask);
      if (ventralIvory) color = applyVentralIvory(color, sampleVentralIvoryMask(v));
      writePixel(baseColor, pixel, color[0], color[1], color[2]);

      const rough = clamp01(
        traits.pbr.roughness
          + (detail - 0.5) * 0.12
          + (patternValue - 0.5) * 0.05
          - ventralMask * 0.075,
      );
      const metal = clamp01(traits.pbr.metalness + (detail - 0.5) * 0.035);
      writePixel(metallicRoughness, pixel, 255, rough * 255, metal * 255);
      heightField[pixel] = patternValue * 0.68 + detail * 0.32;
    }
  }

  const normal = buildNormalMap(heightField, width, height, traits.pbr.normalStrength);
  const metallicRoughnessTexture = createTexture(
    "cowry-metallic-roughness",
    width,
    height,
    metallicRoughness,
    false,
  );
  return {
    baseColor: createArtifact("baseColor", width, height, baseColor, true),
    roughness: {
      kind: "roughness",
      width,
      height,
      bytes: metallicRoughness,
      texture: metallicRoughnessTexture,
    },
    metalness: {
      kind: "metalness",
      width,
      height,
      bytes: metallicRoughness,
      texture: metallicRoughnessTexture,
    },
    normal: createArtifact("normal", width, height, normal, false),
  };
}

function samplePattern(
  algorithm: PatternAlgorithm,
  u: number,
  v: number,
  traits: CowryTraits,
  phase: number,
): number {
  const frequency = traits.pattern.frequency;
  const warp = traits.pattern.warp;
  const patternPhase = phase + traits.pattern.rotation;
  const secondaryScale = traits.pattern.secondaryScale;
  const tau = Math.PI * 2;
  const warpedU = u + Math.sin(v * tau * 2 + patternPhase) * warp * 0.08;
  const warpedV = v + Math.sin(u * tau * (1 + secondaryScale) - patternPhase) * warp * 0.08;
  let value: number;

  switch (algorithm) {
    case "zebra":
      value = 0.5 + 0.5 * Math.sin((warpedU * frequency + Math.sin(v * tau * 3 + patternPhase) * warp * secondaryScale) * tau);
      break;
    case "dots": {
      const columns = Math.max(3, Math.round(frequency * 0.75));
      const rows = Math.max(3, Math.round(frequency * 0.5));
      const cellU = fract(warpedU * columns) - 0.5;
      const cellV = fract(warpedV * rows) - 0.5;
      const distance = Math.sqrt(cellU * cellU + cellV * cellV);
      value = smoothstep(0.3, 0.16, distance);
      break;
    }
    case "speckles": {
      const noise = valueNoise(warpedU * frequency, warpedV * Math.max(3, Math.round(frequency)), `${phase}`);
      value = smoothstep(0.68, 0.9, noise);
      break;
    }
    case "ripples": {
      const circumferentialFrequency = Math.max(3, Math.round(frequency));
      value = 0.5 + 0.5 * Math.sin((warpedV * circumferentialFrequency + Math.sin(u * tau * 2 + patternPhase) * warp * secondaryScale) * tau);
      break;
    }
    case "marble":
      value = 0.5 + 0.5 * Math.sin((warpedU * frequency * 0.55 + valueNoise(u * 3, v * 3, `${patternPhase}`) * warp * (3 + secondaryScale * 2)) * tau);
      break;
    case "mottle": {
      const scale = Math.max(3, Math.round(frequency * 0.58));
      const broad = periodicSmoothNoise(warpedU * scale, warpedV * scale, `${patternPhase}:mottle:broad`, scale);
      const medium = periodicSmoothNoise(warpedU * scale * 2, warpedV * scale * 2, `${patternPhase}:mottle:medium`, scale * 2);
      const fine = periodicSmoothNoise(warpedU * scale * 4, warpedV * scale * 4, `${patternPhase}:mottle:fine`, scale * 4);
      const field = broad * 0.58 + medium * 0.29 + fine * 0.13;
      value = smoothstep(0.28, 0.76, field + Math.sin((u + v) * tau + patternPhase) * warp * 0.08);
      break;
    }
    case "iridescent-bloom": {
      const scale = Math.max(3, Math.round(frequency * 0.48));
      const first = periodicSmoothNoise(warpedU * scale, warpedV * scale, `${patternPhase}:bloom:first`, scale);
      const second = periodicSmoothNoise(
        warpedU * scale * 2 + 7.3,
        warpedV * scale * 2,
        `${patternPhase}:bloom:second`,
        scale * 2,
      );
      const cellular = Math.abs(first * 2 - 1);
      const luminousEdge = 1 - smoothstep(0.08, 0.42, cellular);
      const interior = smoothstep(0.34, 0.78, first * 0.72 + second * 0.28);
      value = clamp01(interior * 0.72 + luminousEdge * (0.48 + secondaryScale * 0.18));
      break;
    }
  }
  return Math.pow(clamp01(value), 1 / Math.max(traits.pattern.contrast, 0.01));
}

function mixPalette(
  primary: readonly number[],
  secondary: readonly number[],
  accent: readonly number[],
  amount: number,
): [number, number, number] {
  const accentMix = smoothstep(0.78, 1, amount);
  const baseMix = smoothstep(0.12, 0.78, amount);
  return [0, 1, 2].map((channel) => {
    const base = lerp(primary[channel] ?? 0, secondary[channel] ?? 0, baseMix);
    return lerp(base, accent[channel] ?? 0, accentMix);
  }) as [number, number, number];
}

function applyFinish(
  finish: FinishDefinition,
  color: readonly [number, number, number],
  detail: number,
): [number, number, number] {
  const detailedTint = finish.tint.map((channel, index) =>
    channel + (detail - 0.5) * (finish.detailTint[index] ?? 0)) as [number, number, number];
  const tinted = color.map((channel, index) =>
    lerp(channel, detailedTint[index] ?? channel, finish.tintStrength)) as [number, number, number];
  const gray = tinted[0] * 0.3 + tinted[1] * 0.59 + tinted[2] * 0.11;
  return tinted.map((channel) => lerp(channel, gray, finish.desaturate)) as [number, number, number];
}

function sampleVentralMask(v: number): number {
  // V = 0..0.5 is dorsal; V = 0.5..1 wraps across the ventral side.
  return clamp01(-Math.sin(v * Math.PI * 2));
}

function applyVentralColorShift(
  color: readonly [number, number, number],
  mask: number,
): [number, number, number] {
  const shadowStrength = mask * 0.1;
  const pearl = [224, 220, 211] as const;
  return color.map((channel, index) =>
    lerp(channel * (1 - shadowStrength), pearl[index] ?? channel, mask * 0.035)) as [number, number, number];
}

function sampleVentralIvoryMask(v: number): number {
  const ventralHemisphere = clamp01(-Math.sin(v * Math.PI * 2));
  return smoothstep(0.08, 0.42, ventralHemisphere);
}

function applyVentralIvory(
  color: readonly [number, number, number],
  mask: number,
): [number, number, number] {
  const ivory = [238, 228, 206] as const;
  return color.map((channel, index) =>
    lerp(channel, ivory[index] ?? channel, mask * 0.94)) as [number, number, number];
}

function buildNormalMap(
  heightField: Float32Array,
  width: number,
  height: number,
  strength: number,
): Uint8Array {
  const bytes = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const left = heightField[y * width + Math.max(0, x - 1)] ?? 0;
      const right = heightField[y * width + Math.min(width - 1, x + 1)] ?? 0;
      const previousY = y === 0 ? height - 2 : y - 1;
      const nextY = y === height - 1 ? 1 : y + 1;
      const down = heightField[previousY * width + x] ?? 0;
      const up = heightField[nextY * width + x] ?? 0;
      const nx = (left - right) * strength * 8;
      const ny = (down - up) * strength * 8;
      const length = Math.hypot(nx, ny, 1);
      writePixel(bytes, y * width + x, (nx / length * 0.5 + 0.5) * 255, (ny / length * 0.5 + 0.5) * 255, (1 / length * 0.5 + 0.5) * 255);
    }
  }
  return bytes;
}

function createArtifact(
  kind: TextureKind,
  width: number,
  height: number,
  bytes: Uint8Array,
  srgb: boolean,
): TextureArtifact {
  const texture = createTexture(`cowry-${kind}`, width, height, bytes, srgb);
  return { kind, width, height, bytes, texture };
}

function createTexture(
  name: string,
  width: number,
  height: number,
  bytes: Uint8Array,
  srgb: boolean,
): THREE.DataTexture {
  const texture = new THREE.DataTexture(bytes, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.name = name;
  texture.flipY = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.needsUpdate = true;
  texture.userData.mimeType = "image/png";
  return texture;
}

function writePixel(bytes: Uint8Array, pixel: number, red: number, green: number, blue: number): void {
  const offset = pixel * 4;
  bytes[offset] = Math.round(red);
  bytes[offset + 1] = Math.round(green);
  bytes[offset + 2] = Math.round(blue);
  bytes[offset + 3] = 255;
}

function valueNoise(u: number, v: number, seed: string): number {
  const x = Math.floor(u * 257);
  const y = Math.floor(fract(v) * 257);
  let hash = Math.imul(x ^ hashString(seed), 0x45d9f3b) ^ Math.imul(y, 0x119de1f3);
  hash = Math.imul(hash ^ (hash >>> 16), 0x45d9f3b);
  return ((hash ^ (hash >>> 16)) >>> 0) / 4294967295;
}

function periodicSmoothNoise(x: number, y: number, seed: string, periodY: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const tx = smoothCurve(fract(x));
  const ty = smoothCurve(fract(y));
  const yPeriod = Math.max(1, Math.round(periodY));
  const sample = (ix: number, iy: number): number => latticeNoise(ix, modulo(iy, yPeriod), seed);
  const lower = lerp(sample(x0, y0), sample(x0 + 1, y0), tx);
  const upper = lerp(sample(x0, y0 + 1), sample(x0 + 1, y0 + 1), tx);
  return lerp(lower, upper, ty);
}

function latticeNoise(x: number, y: number, seed: string): number {
  let hash = Math.imul(x ^ hashString(seed), 0x45d9f3b) ^ Math.imul(y, 0x119de1f3);
  hash = Math.imul(hash ^ (hash >>> 16), 0x45d9f3b);
  return ((hash ^ (hash >>> 16)) >>> 0) / 4294967295;
}

function smoothCurve(value: number): number { return value * value * (3 - 2 * value); }
function modulo(value: number, divisor: number): number { return ((value % divisor) + divisor) % divisor; }

function hashUnit(value: string): number {
  return hashString(value) / 4294967295;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function fract(value: number): number { return value - Math.floor(value); }
function lerp(a: number, b: number, amount: number): number { return a + (b - a) * amount; }
function clamp01(value: number): number { return Math.min(1, Math.max(0, value)); }
function smoothstep(edge0: number, edge1: number, value: number): number {
  const amount = clamp01((value - edge0) / (edge1 - edge0));
  return amount * amount * (3 - 2 * amount);
}
